// The Overview page as data (task eval_admin_runs): which deployed pairings
// are not evaluated, the deployed settings in reading order, and each metric's
// trend as chart series with its prompt-version changes. Pure: tested in
// overviewView.test.ts.

import type { HistoryMarker, HistorySeries } from '../../components/charts';
import { agentLabel } from './agents';
import { metricLabel, modelShortName } from './format';
import type {
  EvalMetric,
  EvalOverview,
  EvalPairingStatus,
  EvalSlot,
  EvalTrendPoint,
  EvalTrendSeries,
} from './types';

/** Pairings that need attention first: not evaluated, then by model, role and suite. */
export function sortPairings(pairings: readonly EvalPairingStatus[]): EvalPairingStatus[] {
  return [...pairings].sort((a, b) => {
    if (a.state !== b.state) return a.state === 'not_evaluated' ? -1 : 1;
    return (
      a.model.localeCompare(b.model) ||
      a.role.localeCompare(b.role) ||
      a.suite.localeCompare(b.suite)
    );
  });
}

/** The headline: how many deployed pairings no stored run covers. */
export function notEvaluatedHeadline(o: Pick<EvalOverview, 'notEvaluated' | 'pairings'>): {
  tone: 'bad' | 'good';
  text: string;
} {
  if (o.pairings.length === 0) {
    return { tone: 'bad', text: 'Nothing deployed has an eval pairing.' };
  }
  if (o.notEvaluated === 0) {
    return { tone: 'good', text: 'Every deployed pairing has a covering run.' };
  }
  const n = o.notEvaluated;
  const total = o.pairings.length;
  if (total === 1) return { tone: 'bad', text: 'The one deployed pairing is not evaluated.' };
  return {
    tone: 'bad',
    text: `${n} of ${total} deployed pairings ${n === 1 ? 'is' : 'are'} not evaluated.`,
  };
}

const SETTING_ORDER = [
  'NEO_MODEL',
  'MODEL_CHOICE',
  'WATCH_REVIEW_MODEL',
  'WATCH_SCREEN_MODEL',
  'OPENROUTER_MODEL',
] as const;

/** The deployed model settings in reading order, the /model choices after NEO_MODEL. */
export function sortSlots(slots: readonly EvalSlot[]): EvalSlot[] {
  return [...slots].sort(
    (a, b) => SETTING_ORDER.indexOf(a.setting) - SETTING_ORDER.indexOf(b.setting)
  );
}

/** What a slot's eval coverage is, in words. */
export function slotCoverageText(slot: EvalSlot): string {
  if (slot.role === null) return 'No eval exercises this setting';
  return slot.role === 'chat' ? 'Neo chat: short and long suites' : 'Watch: long suite';
}

/** The metrics any trend series has, in the server's metric order. */
export function trendMetrics(trends: readonly EvalTrendSeries[]): EvalMetric[] {
  const order: EvalMetric[] = [
    'attacks',
    'helpful',
    'scopeGap',
    'questions',
    'conditions',
    'decisions',
  ];
  const present = new Set(trends.filter((t) => t.points.length > 0).map((t) => t.metric));
  return order.filter((m) => present.has(m));
}

const percent = (x: number): number => Math.round(x * 1000) / 10;

function chartPoint(p: EvalTrendPoint): HistorySeries['points'][number] {
  return {
    t: Date.parse(p.at),
    v: percent(p.rate),
    ...(p.ci95 !== null ? { lo: percent(p.ci95[0]), hi: percent(p.ci95[1]) } : {}),
  };
}

/** The legend's name for a model's runs of part of the suite. */
const PARTIAL_SERIES_SUFFIX = ', part of the suite';

/**
 * One metric's trend as chart series, in percent, a point's 95% interval the
 * band around it. Each model and role gets one line for its runs of the whole
 * suite and, apart from it, one for its runs of part of the suite (--only,
 * --limit, a runner file of a few cases). A rate over a few cases is not the
 * rate over the suite, so the two never share a line: a jump on the
 * whole-suite line is drift, not a change of selection.
 */
export function trendChartSeries(
  trends: readonly EvalTrendSeries[],
  metric: EvalMetric
): HistorySeries[] {
  const out: HistorySeries[] = [];
  const sorted = trends
    .filter((t) => t.metric === metric && t.points.length > 0)
    .sort((a, b) => a.model.localeCompare(b.model) || a.role.localeCompare(b.role));
  for (const t of sorted) {
    // Any agent but Neo's chat is named (TOM-533): the Watch, or an expert run alone.
    const label = `${modelShortName(t.model)}${t.role !== 'chat' ? ` (${agentLabel(t.role)})` : ''}`;
    const full = t.points.filter((p) => p.fullSelection);
    const part = t.points.filter((p) => !p.fullSelection);
    const line = { curve: 'linear' as const, unit: '%' };
    if (full.length > 0) {
      out.push({ ...line, id: `${t.model}:${t.role}`, label, points: full.map(chartPoint) });
    }
    if (part.length > 0) {
      out.push({
        ...line,
        id: `${t.model}:${t.role}:partial`,
        label: `${label}${PARTIAL_SERIES_SUFFIX}`,
        points: part.map(chartPoint),
      });
    }
  }
  return out;
}

/**
 * Prompt-version changes on a metric's chart, one marker per change time. Two
 * series changing at the same run share one marker.
 */
export function promptChangeMarkers(
  trends: readonly EvalTrendSeries[],
  metric: EvalMetric
): HistoryMarker[] {
  const byTime = new Map<number, Set<string>>();
  for (const t of trends) {
    if (t.metric !== metric) continue;
    for (const c of t.promptChanges) {
      const at = Date.parse(c.at);
      const set = byTime.get(at) ?? new Set<string>();
      set.add(c.to);
      byTime.set(at, set);
    }
  }
  return [...byTime.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, labels]) => ({ t, label: [...labels].join(', ') }));
}

/** The chart's accessible name for a metric. */
export function trendAriaLabel(metric: EvalMetric): string {
  return `${metricLabel(metric)} over time, by model`;
}

/** Points from runs of part of the suite, which the chart draws as their own lines. */
export function partialPointCount(trends: readonly EvalTrendSeries[], metric: EvalMetric): number {
  return trends
    .filter((t) => t.metric === metric)
    .reduce((n, t) => n + t.points.filter((p) => !p.fullSelection).length, 0);
}
